/**
 * Shared helpers for AdminImmeubleZones — Batch #14.156 refactor.
 */
import axios from "axios";
import { Loader2, Archive as ArchiveIcon } from "lucide-react";
import { API, authH } from "@/api";
import { fmtCAD as dollars } from "@/lib/format";
export { API, authH, dollars };

// Batch #14.104 — Helper centralisé pour archiver n'importe quel item Immeuble
// Types supportés : lease | rl31 | rent_increase | credit_check
export async function archiveImmeubleItem({ masterId, entityType, entityId, label, snapshot }) {
  return axios.post(
    `${API}/admin/immeuble/archives/add`,
    { entity_type: entityType, entity_id: entityId, master_id: masterId, label, snapshot: snapshot || null },
    { headers: authH() },
  );
}

// Batch #14.105 — Mapping méthodes paiement vers libellés UX
export const PAYMENT_METHOD_LABEL = {
  interac: "Interac e-Transfert",
  e_transfert: "e-Transfert",
  card: "Carte de crédit",
  credit_card: "Carte de crédit",
  cash: "Comptant",
  cheque: "Chèque",
  transfer: "Virement",
  other: "Autre",
  manual: "Manuel",
};

// Bouton « Terminer » uniformisé pour tous les blocs (Vérifications, Demandes, Bail)
export function TerminerButton({ onClick, busy, testId, size = "sm" }) {
  const sizeCls = size === "sm" ? "px-2.5 py-1 text-[10px]" : "px-3 py-1.5 text-xs";
  return (
    <button
      onClick={onClick}
      disabled={busy}
      className={`${sizeCls} rounded bg-amber-600 hover:bg-amber-700 text-white font-semibold inline-flex items-center gap-1 disabled:opacity-50`}
      data-testid={testId}
      title="Terminer cette demande (archive 15 jours puis suppression)"
    >
      {busy
        ? <Loader2 className="w-3 h-3 animate-spin" />
        : <ArchiveIcon className="w-3 h-3" />}
      Terminer
    </button>
  );
}

export function ZoneHeader({ index, title, icon: Icon, tone = "emerald", subtitle }) {
  const toneCls = {
    emerald: "from-emerald-50 to-white text-emerald-700 bg-emerald-100",
    blue: "from-blue-50 to-white text-blue-700 bg-blue-100",
    amber: "from-amber-50 to-white text-amber-700 bg-amber-100",
    indigo: "from-indigo-50 to-white text-indigo-700 bg-indigo-100",
  }[tone];
  const [bgFrom, _bgTo, textColor, iconBg] = toneCls.split(" ");
  return (
    <header className={`px-5 py-3 border-b border-slate-100 bg-gradient-to-r ${bgFrom} ${_bgTo}`}>
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg ${iconBg} ${textColor} flex items-center justify-center`}>
          <Icon className="w-4.5 h-4.5" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-slate-400">Zone {index}</span>
            <span className="text-slate-300">·</span>
            {title}
          </h2>
          {subtitle && <p className="text-[11px] text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
    </header>
  );
}

export function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold text-slate-600 block mb-1">{label}</span>
      {children}
    </label>
  );
}

export function StatusBadge({ status }) {
  if (!status) return null;
  const map = {
    pending: ["bg-amber-100 text-amber-800", "En attente"],
    pending_signatures: ["bg-amber-100 text-amber-800", "En attente signatures"],
    completed: ["bg-emerald-100 text-emerald-800", "Complété"],
    fully_signed: ["bg-emerald-100 text-emerald-800", "Signé"],
    letter_ready: ["bg-blue-100 text-blue-800", "Lettre prête"],
    paid: ["bg-emerald-100 text-emerald-800", "Payé"],
  };
  const [cls, label] = map[status] || ["bg-slate-100 text-slate-700", status];
  return <span className={`px-1.5 py-0 rounded text-[9px] font-semibold ${cls}`}>{label}</span>;
}

export function PaymentBadge({ status, amount }) {
  if (!status || status === "none") return null;
  const map = {
    pending: ["bg-amber-100 text-amber-800", "Paiement en attente"],
    paid: ["bg-emerald-100 text-emerald-800", `Payé ${dollars(amount)}`],
    failed: ["bg-rose-100 text-rose-800", "Échec paiement"],
  };
  const [cls, label] = map[status] || [];
  if (!label) return null;
  return <span className={`px-1.5 py-0 rounded text-[9px] font-semibold ${cls}`}>{label}</span>;
}

export function KPI({ label, value, tone = "slate", testId }) {
  const toneMap = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    "emerald-strong": "bg-emerald-100 border-emerald-300 text-emerald-900 ring-2 ring-emerald-200",
    blue: "bg-blue-50 border-blue-200 text-blue-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
    "rose-strong": "bg-rose-100 border-rose-300 text-rose-900 ring-2 ring-rose-200",
    slate: "bg-slate-50 border-slate-200 text-slate-900",
  };
  return (
    <div className={`rounded-lg border p-3 ${toneMap[tone] || toneMap.slate}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</div>
      <div className="text-base font-bold font-mono mt-1">{value}</div>
    </div>
  );
}
